import { useEffect, useState } from "react";
import { Globe, Gamepad2, ChevronDown } from "lucide-react";
import { api } from "../lib/api";

const Dot = () => (
  <span className="relative flex h-2 w-2 shrink-0">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2ECC71] opacity-70" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2ECC71]" />
  </span>
);

const DiscordGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M20.3 4.37A19.8 19.8 0 0 0 15.4 2.9l-.24.49a17.6 17.6 0 0 1 4.2 1.33 15.6 15.6 0 0 0-13.1 0 17.6 17.6 0 0 1 4.2-1.33l-.24-.49A19.8 19.8 0 0 0 3.7 4.37C.86 8.55.08 12.63.47 16.65a19.9 19.9 0 0 0 6.03 3.04l.49-.68a13 13 0 0 1-1.95-.94c.16-.12.32-.24.47-.37a13.6 13.6 0 0 0 11.98 0c.15.13.31.25.47.37-.62.37-1.28.68-1.95.94l.49.68a19.9 19.9 0 0 0 6.03-3.04c.46-4.66-.78-8.7-3.72-12.28ZM8.52 14.33c-1.16 0-2.12-1.06-2.12-2.37s.94-2.37 2.12-2.37 2.13 1.07 2.11 2.37c0 1.31-.94 2.37-2.11 2.37Zm6.96 0c-1.16 0-2.12-1.06-2.12-2.37s.94-2.37 2.12-2.37 2.13 1.07 2.11 2.37c0 1.31-.93 2.37-2.11 2.37Z" /></svg>
);

// Total Online Network: website presence + Discord online + live game players.
const OnlineNow = ({ collapsed = false }) => {
  const [n, setN] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/network/online").then((r) => setN(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  if (!n) return null;

  if (collapsed) {
    return (
      <div title={`${n.total} online across the network — ${n.web} site, ${n.discord} Discord, ${n.game} in-game`} data-testid="online-now" className="w-full p-2 flex justify-center">
        <Dot />
      </div>
    );
  }

  const Row = ({ icon: Icon, label, value, color, testid }) => (
    <div className="flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-[#94A3B8]"><Icon className="w-3 h-3" style={{ color }} /> {label}</span>
      <span data-testid={testid} className="font-heading font-black text-[11px] tabular-nums" style={{ color }}>{value}</span>
    </div>
  );

  return (
    <div data-testid="online-now" className="w-full bg-[#13151B] border border-[#2A2E3D] tactical-frame-sm">
      <button
        data-testid="network-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#191C24] transition-colors"
      >
        <Dot />
        <span className="font-stencil font-bold uppercase text-xs text-[#F1F5F9]">Network Online</span>
        <span data-testid="network-total" className="ml-auto font-heading font-black text-base text-[#2ECC71] tabular-nums">{n.total}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-[#64748B] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="network-breakdown" className="px-3 pb-2.5 space-y-1 border-t border-[#2A2E3D] pt-2">
          <Row icon={Globe} label="Website" value={n.web} color="#D4A94A" testid="network-web" />
          <Row icon={DiscordGlyph} label="Discord" value={n.discord} color="#5865F2" testid="network-discord" />
          <Row icon={Gamepad2} label="In-game" value={n.game} color="#2ECC71" testid="network-game" />
        </div>
      )}
    </div>
  );
};

export default OnlineNow;
